import React, { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { authApi, wishlistApi } from "@/lib/api";
import { MAX_RECENTLY_VIEWED, snapshotProduct } from "@/lib/recentlyViewed";

const StoreCtx = createContext(null);

const readLS = (k, fallback) => {
  try {
    const v = localStorage.getItem(k);
    return v ? JSON.parse(v) : fallback;
  } catch {
    return fallback;
  }
};

const hasStoredSession = () => {
  try {
    return Boolean(localStorage.getItem("oori_token") && localStorage.getItem("oori_user"));
  } catch {
    return false;
  }
};

/** Only clear login on a real auth rejection — never on deploy/cold-start/network blips. */
const isUnauthorizedAuthError = (err) => {
  const status = err?.response?.status;
  return status === 401 || status === 403;
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

async function refreshSessionUser(retries = 2) {
  let lastError;
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      const res = await authApi.me();
      return res.data;
    } catch (err) {
      lastError = err;
      if (isUnauthorizedAuthError(err)) throw err;
      if (attempt < retries) await sleep(800 * (attempt + 1));
    }
  }
  throw lastError;
}

const mergeWishlist = (localItems = [], remoteItems = []) => {
  const map = new Map();
  [...remoteItems, ...localItems].forEach((item) => {
    if (!item?.id) return;
    map.set(item.id, snapshotProduct(item));
  });
  return Array.from(map.values());
};

export const StoreProvider = ({ children }) => {
  const [cart, setCart] = useState(() => readLS("oori_cart", []));
  const [wishlist, setWishlist] = useState(() => readLS("oori_wishlist", []));
  const [recentlyViewed, setRecentlyViewed] = useState(() => readLS("oori_recently_viewed", []));
  const [cartOpen, setCartOpen] = useState(false);
  const [user, setUser] = useState(() => readLS("oori_user", null));
  // Cached sessions stay logged-in immediately; only block UI when we have no session yet.
  const [authLoading, setAuthLoading] = useState(() => !hasStoredSession());
  const wishlistSyncReady = useRef(false);
  const wishlistPersistTimer = useRef(null);
  const intentionalLogout = useRef(false);

  useEffect(() => { localStorage.setItem("oori_cart", JSON.stringify(cart)); }, [cart]);
  useEffect(() => { localStorage.setItem("oori_wishlist", JSON.stringify(wishlist)); }, [wishlist]);
  useEffect(() => { localStorage.setItem("oori_recently_viewed", JSON.stringify(recentlyViewed)); }, [recentlyViewed]);

  useEffect(() => {
    if (user) {
      localStorage.setItem("oori_user", JSON.stringify(user));
      return;
    }
    // Never wipe a stored token just because user state is briefly null during boot.
    if (intentionalLogout.current || !localStorage.getItem("oori_token")) {
      localStorage.removeItem("oori_user");
      localStorage.removeItem("oori_token");
    }
  }, [user]);

  useEffect(() => {
    const token = localStorage.getItem("oori_token");
    if (!token) {
      setAuthLoading(false);
      return;
    }

    let cancelled = false;
    const timeout = setTimeout(() => {
      if (!cancelled) setAuthLoading(false);
    }, 12000);

    refreshSessionUser()
      .then((nextUser) => {
        if (!cancelled) setUser(nextUser);
      })
      .catch((err) => {
        if (cancelled) return;
        // Keep users signed in across deploys / API blips. Only hard-logout on 401/403.
        if (isUnauthorizedAuthError(err)) {
          intentionalLogout.current = true;
          localStorage.removeItem("oori_token");
          localStorage.removeItem("oori_user");
          setUser(null);
        }
      })
      .finally(() => {
        clearTimeout(timeout);
        if (!cancelled) setAuthLoading(false);
      });

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, []);

  // Pull server wishlist for signed-in users and merge with any local saves.
  useEffect(() => {
    if (!user?.id) {
      wishlistSyncReady.current = false;
      return undefined;
    }

    let active = true;
    wishlistSyncReady.current = false;
    wishlistApi
      .get()
      .then((res) => {
        if (!active) return;
        const remote = res.data?.items || [];
        setWishlist((local) => {
          const merged = mergeWishlist(local, remote);
          // Persist merged list so reminders can see guest-saved items after login.
          wishlistApi.save(merged).catch(() => {});
          return merged;
        });
      })
      .catch(() => {})
      .finally(() => {
        if (active) wishlistSyncReady.current = true;
      });

    return () => {
      active = false;
    };
  }, [user?.id]);

  // Push wishlist changes to the server for reminder emails (skip empty-clear races during hydrate).
  useEffect(() => {
    if (!user?.id || !wishlistSyncReady.current) return undefined;
    if (wishlistPersistTimer.current) clearTimeout(wishlistPersistTimer.current);
    wishlistPersistTimer.current = setTimeout(() => {
      wishlistApi.save(wishlist).catch(() => {});
    }, 400);
    return () => {
      if (wishlistPersistTimer.current) clearTimeout(wishlistPersistTimer.current);
    };
  }, [wishlist, user?.id]);

  const addToCart = useCallback((product, size, qty = 1, selectedImage) => {
    const available = Number(product?.stock_count ?? 0);
    const purchasable = product?.listing_status === "active" && product?.is_active !== false && available > 0;
    if (!purchasable) {
      toast.error("This piece is no longer available");
      return;
    }
    if (qty > available) {
      toast.error(`Only ${available} left in stock`);
      return;
    }
    const image =
      selectedImage ||
      product?.images?.[0] ||
      product?.image ||
      "";
    setCart((prev) => {
      // Include image in key so different photos (e.g. colour shots) stay distinct lines.
      const key = `${product.id}-${size}-${image}`;
      const existing = prev.find((i) => i.key === key);
      const stockCap = Number(product.stock_count ?? existing?.stock_count ?? 0);
      if (existing) {
        const nextQty = existing.quantity + qty;
        const capped = stockCap > 0 ? Math.min(nextQty, stockCap) : nextQty;
        if (capped < nextQty) {
          toast.error(`Only ${stockCap} left in stock`);
        }
        return prev.map((i) =>
          i.key === key
            ? { ...i, quantity: capped, stock_count: stockCap || i.stock_count, image }
            : i
        );
      }
      return [
        ...prev,
        {
          key,
          product_id: product.id,
          name: product.name,
          image,
          price: product.price,
          color: product.color,
          size,
          quantity: qty,
          stock_count: stockCap,
        },
      ];
    });
    setCartOpen(true);
    toast.success(`${product.name} added to bag`);
  }, []);

  const updateQty = useCallback((key, qty) => {
    setCart((prev) =>
      prev.flatMap((i) => {
        if (i.key !== key) return [i];
        if (qty <= 0) return [];
        let next = qty;
        const stockCap = Number(i.stock_count);
        if (stockCap > 0 && next > stockCap) {
          toast.error(`Only ${stockCap} left in stock`);
          next = stockCap;
        }
        return [{ ...i, quantity: next }];
      })
    );
  }, []);

  const removeFromCart = useCallback((key) => {
    setCart((prev) => prev.filter((i) => i.key !== key));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const toggleWishlist = useCallback((product) => {
    setWishlist((prev) => {
      const exists = prev.find((p) => p.id === product.id);
      if (exists) {
        toast("Removed from wishlist");
        return prev.filter((p) => p.id !== product.id);
      }
      toast.success("Saved to wishlist");
      return [...prev, snapshotProduct(product)];
    });
  }, []);

  const inWishlist = useCallback((id) => wishlist.some((p) => p.id === id), [wishlist]);

  const trackRecentlyViewed = useCallback((product) => {
    if (!product?.id) return;
    const snap = snapshotProduct(product);
    setRecentlyViewed((prev) => {
      const next = [snap, ...prev.filter((p) => p.id !== product.id)];
      return next.slice(0, MAX_RECENTLY_VIEWED);
    });
  }, []);

  const subtotal = useMemo(
    () => cart.reduce((s, i) => s + i.price * i.quantity, 0),
    [cart]
  );
  const itemCount = useMemo(
    () => cart.reduce((s, i) => s + i.quantity, 0),
    [cart]
  );

  const setSession = useCallback((token, userData) => {
    intentionalLogout.current = false;
    localStorage.setItem("oori_token", token);
    localStorage.setItem("oori_user", JSON.stringify(userData));
    setUser(userData);
    toast.success(`Welcome, ${userData?.name?.split(" ")?.[0] || "back"}`);
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await authApi.login({ email, password });
    setSession(res.data.access_token, res.data.user);
    return res.data.user;
  }, [setSession]);

  const register = useCallback(async (name, email, phone, password) => {
    const res = await authApi.register({ name, email, phone, password });
    return res.data;
  }, []);

  const verifyRegistrationOtp = useCallback(async (email, otp) => {
    const res = await authApi.verifyOtp({ email, otp });
    setSession(res.data.access_token, res.data.user);
    return res.data.user;
  }, [setSession]);

  const resendRegistrationOtp = useCallback(async (email) => {
    const res = await authApi.resendOtp({ email });
    return res.data;
  }, []);

  const logout = useCallback(() => {
    intentionalLogout.current = true;
    localStorage.removeItem("oori_token");
    localStorage.removeItem("oori_user");
    setUser(null);
    toast("Signed out");
  }, []);

  const updateProfile = useCallback(async (name, phone) => {
    const res = await authApi.updateProfile({ name, phone });
    setUser(res.data);
    toast.success("Profile updated");
    return res.data;
  }, []);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    const res = await authApi.changePassword({
      current_password: currentPassword,
      new_password: newPassword,
    });
    toast.success("Password updated");
    return res.data;
  }, []);

  /** Re-add past order line items to the bag (best-effort; skips incomplete rows). */
  const reorderItems = useCallback((items = []) => {
    let added = 0;
    setCart((prev) => {
      let next = [...prev];
      for (const item of items) {
        const productId = item.product_id || item.id;
        const size = item.size;
        const qty = Math.max(1, Number(item.quantity) || 1);
        const image = item.image || "";
        if (!productId || !size) continue;
        const key = `${productId}-${size}-${image}`;
        const existing = next.find((i) => i.key === key);
        if (existing) {
          next = next.map((i) =>
            i.key === key ? { ...i, quantity: i.quantity + qty } : i
          );
        } else {
          next.push({
            key,
            product_id: productId,
            name: item.name || "Item",
            image,
            price: Number(item.price) || 0,
            color: item.color || "",
            size,
            quantity: qty,
            stock_count: Number(item.stock_count) || 99,
          });
        }
        added += 1;
      }
      return next;
    });
    if (added > 0) {
      setCartOpen(true);
      toast.success(added === 1 ? "Item added to bag" : `${added} items added to bag`);
    } else {
      toast.error("Could not add items from this order");
    }
  }, []);

  const isAdmin = user?.role === "admin";

  const value = useMemo(
    () => ({
      cart,
      wishlist,
      recentlyViewed,
      cartOpen,
      setCartOpen,
      addToCart,
      updateQty,
      removeFromCart,
      clearCart,
      toggleWishlist,
      inWishlist,
      trackRecentlyViewed,
      subtotal,
      itemCount,
      user,
      login,
      register,
      verifyRegistrationOtp,
      resendRegistrationOtp,
      updateProfile,
      changePassword,
      reorderItems,
      logout,
      authLoading,
      isAdmin,
    }),
    [
      cart,
      wishlist,
      recentlyViewed,
      cartOpen,
      addToCart,
      updateQty,
      removeFromCart,
      clearCart,
      toggleWishlist,
      inWishlist,
      trackRecentlyViewed,
      subtotal,
      itemCount,
      user,
      login,
      register,
      verifyRegistrationOtp,
      resendRegistrationOtp,
      updateProfile,
      changePassword,
      reorderItems,
      logout,
      authLoading,
      isAdmin,
    ]
  );
  return <StoreCtx.Provider value={value}>{children}</StoreCtx.Provider>;
};

export const useStore = () => {
  const ctx = useContext(StoreCtx);
  if (!ctx) throw new Error("useStore must be used inside StoreProvider");
  return ctx;
};
